import * as Sentry from '@sentry/react';
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, ReactNode } from 'react';
import { User, AuthContextType, CompanyOption } from '../types';
import type { B2BBuyer } from '../types/b2b';
import {
  loginUser,
  getCurrentUser,
  getCurrentBuyer,
  logoutUser,
  setupTokenRefreshTimer,
  onTokenRefresh,
  fetchMyCompanies,
  switchCompany as switchCompanyRequest,
} from '../lib/auth';
import { getActiveCompanyId, resetActiveCompanyCache } from '../lib/activeCompany';
import { fetchCompanyProfile, type CompanyProfile } from '../lib/companyProfile';
import { clearSessionCaches } from '../lib/sessionCache';
import { createContextLogger, logger } from '../lib/logger';

const log = createContextLogger('AuthContext');

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [buyer, setBuyer] = useState<B2BBuyer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [connectionQuality, setConnectionQuality] = useState<'good' | 'slow' | 'offline'>('good');
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);

  // Bumped whenever the stored token is replaced, so the active company is
  // re-read from the new claim. A background refresh can silently revert
  // admin_id after the super admin revokes a grant.
  const [tokenVersion, setTokenVersion] = useState(0);

  // tokenVersion is not read here; it is a dependency so the claim is re-read
  // after a background refresh replaces the stored token.
  const activeCompanyId = useMemo(
    () => (user?.role === 'admin' ? getActiveCompanyId() || user.id : null),
    [user, tokenVersion],
  );
  const isActingAsOtherCompany = !!user && user.role === 'admin' && activeCompanyId !== user.id;

  // Mirrored into a ref so the token-refresh listener, registered once on
  // mount, can compare against the company that was active before the refresh.
  const activeCompanyIdRef = useRef<string | null>(activeCompanyId);
  useEffect(() => {
    activeCompanyIdRef.current = activeCompanyId;
  }, [activeCompanyId]);

  const loadCompanyContext = useCallback(async (currentUser: User | null) => {
    if (!currentUser || currentUser.role !== 'admin') {
      setCompanies([]);
      setCompanyProfile(null);
      return;
    }

    // A failed request must not empty an already populated switcher, or a
    // transient network error looks identical to having no company access.
    // Revocations still propagate: those come back as a successful shorter list,
    // and the server re-checks the grant on every switch regardless.
    const { companies: list, ok } = await fetchMyCompanies();
    if (ok) setCompanies(list);

    const targetId = getActiveCompanyId() || currentUser.id;
    if (targetId === currentUser.id) {
      setCompanyProfile(null);
      return;
    }

    setCompanyProfile(await fetchCompanyProfile(targetId));
  }, []);

  // The overlay is derived from the token claim rather than set at switch time,
  // so a revoked grant drops it automatically on the next refresh.
  useEffect(() => {
    if (!user || user.role !== 'admin' || !activeCompanyId || activeCompanyId === user.id) {
      setCompanyProfile(null);
      return;
    }

    let isStale = false;
    fetchCompanyProfile(activeCompanyId).then(profile => {
      if (!isStale) setCompanyProfile(profile);
    });
    return () => {
      isStale = true;
    };
  }, [user, activeCompanyId]);

  useEffect(() => {
    initializeAuth();
    setIsOffline(!navigator.onLine);
    setConnectionQuality(navigator.onLine ? 'good' : 'offline');

    const cleanupRefreshTimer = setupTokenRefreshTimer();
    const cleanupRefreshListener = onTokenRefresh(() => {
      const previous = activeCompanyIdRef.current;
      resetActiveCompanyCache();
      const next = getActiveCompanyId();

      // The server reverts admin_id on refresh once a grant is revoked. Dozens
      // of hooks memoise the tenant on the user object, which does not change
      // here, so reload rather than try to invalidate each of them.
      if (previous && next && previous !== next) {
        log.warn('Active company changed on token refresh, reloading', { previous, next });
        clearSessionCaches();
        window.location.reload();
        return;
      }

      setTokenVersion(v => v + 1);
    });

    const handleOnline = () => {
      setIsOffline(false);
      setConnectionQuality('good');
    };

    const handleOffline = () => {
      setIsOffline(true);
      setConnectionQuality('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      cleanupRefreshTimer();
      cleanupRefreshListener();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const initializeAuth = async () => {
    try {
      setIsLoading(true);
      const currentUser = await getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        setBuyer(null);
        logger.setUserId(currentUser.id);
        Sentry.setUser({ id: currentUser.id, email: currentUser.email, username: currentUser.username });
        await loadCompanyContext(currentUser);
      } else {
        const currentBuyer = await getCurrentBuyer();
        if (currentBuyer) {
          setBuyer(currentBuyer);
          setUser(null);
          logger.setUserId(currentBuyer.id);
          Sentry.setUser({ id: currentBuyer.id, email: currentBuyer.email });
        }
      }
    } catch (error) {
      log.error('Auth initialization failed', { error: String(error) });
      Sentry.captureException(error);
      setError('Failed to initialize authentication');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string; redirectTo?: string }> => {
    try {
      setError(null);
      const result = await loginUser(email, password);

      if (result.success && result.user) {
        setUser(result.user);
        setBuyer(null);
        logger.setUserId(result.user.id);
        Sentry.setUser({ id: result.user.id, email: result.user.email, username: result.user.username });
        await loadCompanyContext(result.user);
      } else if (result.success && result.buyer) {
        setBuyer(result.buyer);
        setUser(null);
        logger.setUserId(result.buyer.id);
        Sentry.setUser({ id: result.buyer.id, email: result.buyer.email });
      } else {
        setError(result.error || 'Login failed');
      }

      return result;
    } catch (error) {
      log.error('Login failed', { error: String(error) });
      Sentry.captureException(error, { tags: { operation: 'login' } });
      const errorMessage = 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    await logoutUser();
    setUser(null);
    setBuyer(null);
    setCompanies([]);
    setCompanyProfile(null);
    setError(null);
    Sentry.setUser(null);
  };

  const refreshUserData = async () => {
    try {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        // The overlay effect re-applies company fields off the new user object,
        // so nothing to do here beyond refreshing the identity.
        setUser(currentUser);
      }
    } catch (error) {
      log.error('Failed to refresh user data', { error: String(error) });
    }
  };

  const switchCompany = async (targetAdminId: string) => {
    const result = await switchCompanyRequest(targetAdminId);
    if (!result.success) return result;

    // A hard reload is the only reliable way to drop tenant-scoped state held
    // in the many hooks and contexts that read the tenant at mount time.
    clearSessionCaches();
    window.location.reload();
    return result;
  };

  const isAuthenticated = !!user || !!buyer;
  const isSuperAdmin = user?.role === 'super_admin';
  const hasActiveSubscription = true;

  // Company identity follows the active company; who you are does not.
  // Gated on isActingAsOtherCompany so a revoked grant (which reverts admin_id
  // on the next token refresh) can never keep serving the old company's VAT,
  // IBAN or logo from a stale overlay.
  const effectiveUser = useMemo<User | null>(
    () => (user && companyProfile && isActingAsOtherCompany ? { ...user, ...companyProfile } : user),
    [user, companyProfile, isActingAsOtherCompany],
  );

  const value: AuthContextType = {
    user: effectiveUser,
    buyer,
    isBuyer: !!buyer,
    login,
    logout,
    isLoading,
    error,
    refreshUserData,
    isAuthenticated,
    isSuperAdmin,
    hasActiveSubscription,
    companies,
    activeCompanyId,
    canSwitchCompany: user?.role === 'admin' && companies.length > 1,
    switchCompany,
    token: localStorage.getItem('auth_token'),
    subscription: null,
    isOffline,
    connectionQuality,
    setError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
